import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { API_BASE_URL, SERVICE_NAME } from '@/config/api'

// API 클라이언트 설정
// 인터페이스 명세서를 받으면 baseURL과 인터셉터를 설정합니다
// baseURL·로컬 기본값은 src/config/api.ts 에서만 관리
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30초로 증가 (코치 삭제 등 시간이 오래 걸리는 작업 대응)
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // CORS 문제 해결을 위해 임시로 false (JWT 토큰 사용 시 불필요)
  maxRedirects: 0, // 리다이렉트를 따라가지 않도록 설정 (OAuth 리다이렉트 방지)
})

// Request 인터셉터
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (config.headers) {
      config.headers['X-Service-Name'] = SERVICE_NAME
    }
    // 토큰 등 인증 정보 추가
    const token = localStorage.getItem('token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Response 인터셉터
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  (error: AxiosError) => {
    // 에러 처리
    // 401 에러는 UserContext에서 처리하도록 에러를 그대로 전달
    // (자동 리다이렉트하지 않음)
    if (error.code === 'ECONNABORTED') {
      // 타임아웃 에러
      console.error('Request timeout:', error.message)
      // 타임아웃 에러는 그대로 전달 (컴포넌트에서 처리)
    } else if (error.response?.status === 302) {
      // 302 리다이렉트는 OAuth 리다이렉트일 수 있음
      // 백엔드 오류로 인해 인증되지 않은 요청이 Google OAuth로 리다이렉트되는 경우
      const currentPath = window.location.pathname
      
      // 로그인 페이지나 OAuth 콜백 페이지가 아닌 경우에만 로그인 페이지로 리다이렉트
      // (OAuth 콜백 직후의 fetchCurrentUser 실패는 토큰이 유효할 수 있으므로 처리하지 않음)
      if (currentPath !== '/login' && 
          !currentPath.startsWith('/login/') &&
          currentPath !== '/auth/callback' &&
          !currentPath.startsWith('/auth/callback')) {
        // 토큰 삭제 (백엔드가 인증을 처리하지 못하는 상황)
        localStorage.removeItem('token')
        // 로그인 페이지로 리다이렉트
        window.location.href = '/login'
        return Promise.reject(new Error('Redirect to login'))
      }
      
      return Promise.reject(new Error('Redirect'))
    } else if (error.code === 'ERR_NETWORK' || !error.response) {
      // 네트워크 에러 또는 리다이렉트로 인한 응답 없음
      // Google OAuth로 리다이렉트되는 경우를 감지
      const errorMessage = error.message || ''
      const requestUrl = error.config?.url || ''
      const responseURL = (error.request as XMLHttpRequest)?.responseURL || ''
      const isOAuthRedirect = 
        errorMessage.includes('accounts.google.com') ||
        requestUrl.includes('accounts.google.com') ||
        responseURL.includes('accounts.google.com')
      
      const token = localStorage.getItem('token')
      const currentPath = window.location.pathname
      
      // 로그인 페이지에서는 에러를 조용히 처리 (CORS 에러 방지)
      if (currentPath === '/login' || currentPath.startsWith('/login/')) {
        return Promise.reject(new Error('Network Error'))
      }
      
      // Google OAuth로 리다이렉트되는 경우 (백엔드 오류로 인한 인증 실패)
      // 하지만 handleOAuthCallback 직후에는 토큰이 유효할 수 있으므로 조심해야 함
      if (isOAuthRedirect) {
        // OAuth 리다이렉트인 경우에만 처리
        // 단, 로그인 페이지나 OAuth 콜백 페이지에서는 처리하지 않음
        if (currentPath !== '/login' && 
            !currentPath.startsWith('/login/') && 
            currentPath !== '/auth/callback' &&
            !currentPath.startsWith('/auth/callback')) {
          // 토큰 삭제 (백엔드가 인증을 처리하지 못하는 상황)
          localStorage.removeItem('token')
          // 로그인 페이지로 리다이렉트
          window.location.href = '/login'
          return Promise.reject(new Error('Redirect to login'))
        }
      }
      
      // ERR_NETWORK 에러가 발생했지만 OAuth 리다이렉트가 아닌 경우
      // 토큰이 있어도 네트워크 문제일 수 있으므로 토큰을 삭제하지 않음
      // (handleOAuthCallback 직후의 fetchCurrentUser 실패는 백엔드가 아직 토큰을 인식하지 못했을 수 있음)
      
      // ERR_NETWORK 에러가 발생했지만 OAuth 리다이렉트가 아니고 토큰도 없는 경우
      // 일반적인 네트워크 오류일 수 있으므로 조용히 처리
      if (!token) {
        // 토큰이 없으면 조용히 처리 (로그인 페이지에서 처리)
        // console.warn('No authentication token found.')
      }
    }
    return Promise.reject(error)
  }
)

export default apiClient

